import React, { useState } from 'react';
import { Modal, Button, Typography, Form, Input, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

interface MaintenanceSetupModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const MaintenanceSetupModal: React.FC<MaintenanceSetupModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const { t } = useTranslation('maintenance');
  const { } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setupMaintenanceMutation = useMutation({
    mutationFn: async (values: any) => {
      // TODO: Implement actual maintenance setup API call
      // This is a placeholder for now
      console.log('Setting up maintenance with values:', values);
      return Promise.resolve();
    },
    onSuccess: () => {
      message.success(t('setup.success', 'Maintenance system configured successfully!'));
      queryClient.invalidateQueries({ queryKey: ['maintenance-setup'] });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error setting up maintenance:', error);
      message.error(t('setup.error', 'Failed to setup maintenance system. Please try again.'));
    }
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      await setupMaintenanceMutation.mutateAsync(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('setup.title', 'Setup Maintenance System')}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="setup"
          type="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('setup.button', 'Setup Maintenance')}
        </Button>
      ]}
      width={500}
    >
      <div style={{ padding: '20px 0' }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          {t('setup.description', 'Configure Maintenance Settings')}
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          {t('setup.details', 'Set up your maintenance system with basic configuration options.')}
        </Text>
        
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            priority: 'medium',
            status: 'pending'
          }}
        >
          <Form.Item
            name="maintenanceType"
            label={t('setup.maintenanceType', 'Default Maintenance Type')}
            rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
          >
            <Select placeholder={t('setup.selectType', 'Select maintenance type')}>
              <Option value="preventive">{t('setup.types.preventive', 'Preventive')}</Option>
              <Option value="corrective">{t('setup.types.corrective', 'Corrective')}</Option>
              <Option value="emergency">{t('setup.types.emergency', 'Emergency')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="defaultPriority"
            label={t('setup.defaultPriority', 'Default Priority')}
            rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
          >
            <Select placeholder={t('setup.selectPriority', 'Select default priority')}>
              <Option value="low">{t('setup.priorities.low', 'Low')}</Option>
              <Option value="medium">{t('setup.priorities.medium', 'Medium')}</Option>
              <Option value="high">{t('setup.priorities.high', 'High')}</Option>
              <Option value="urgent">{t('setup.priorities.urgent', 'Urgent')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('setup.setupDescription', 'Setup Description')}
          >
            <Input.TextArea 
              rows={3}
              placeholder={t('setup.descriptionPlaceholder', 'Optional description of the maintenance setup')}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default MaintenanceSetupModal;
